'use client'

import * as React from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'

/**
 * AppliedFilters — overview row of the filters currently narrowing a list.
 * Each chip removes its own filter on click; Clear all restores the page's
 * DEFAULT filters (which may deliberately hide inactive records), not "no
 * filters". Renders nothing when no non-default filter is applied.
 */

export interface AppliedFilter {
    /** Stable identity for the chip, e.g. `tag:${id}`. */
    key: string
    /** Full label including the facet name, e.g. "Tag: Board". */
    label: string
    onRemove: () => void
}

export interface AppliedFiltersProps {
    filters: AppliedFilter[]
    onClearAll: () => void
    className?: string
}

export function AppliedFilters({ filters, onClearAll, className }: AppliedFiltersProps) {
    if (filters.length === 0) return null

    return (
        <div className={cn('flex flex-wrap items-center gap-2', className)} role="group" aria-label="Applied filters">
            {filters.map((filter) => (
                <button
                    key={filter.key}
                    type="button"
                    aria-label={`Remove filter: ${filter.label}`}
                    onClick={filter.onRemove}
                    className={cn(
                        'inline-flex h-8 items-center gap-1.5 pl-3 pr-2 rounded-full border text-sm font-medium leading-5 transition-colors duration-200',
                        'bg-[var(--sage-50)] border-[var(--sage-300)] text-[var(--text-sage)] hover:bg-[var(--sage-100)]',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-2'
                    )}
                >
                    <span className="truncate max-w-52">{filter.label}</span>
                    <X className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                </button>
            ))}
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[var(--text-secondary)]"
                onClick={onClearAll}
            >
                Clear all
            </Button>
        </div>
    )
}
